'use client';

import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import Modal from '@/components/hrm/shared/Modal';
import {
  fetchActiveSessions,
  logoutAllSessions,
  logoutSession,
} from '@/services/auth.service';
import { useAuthStore } from '@/stores/auth-store';
import { AuthApiError } from '@/lib/api/auth-client';
import type { AuthSession } from '@/types/auth';

interface SessionsPanelProps {
  onClose: () => void;
  onLoggedOut?: () => void;
}

export default function SessionsPanel({ onClose, onLoggedOut }: SessionsPanelProps) {
  const sessionId = useAuthStore((s) => s.sessionId);
  const logoutStore = useAuthStore((s) => s.logout);
  const [sessions, setSessions] = useState<AuthSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [actionId, setActionId] = useState<string | null>(null);

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await fetchActiveSessions();
      setSessions(rows);
    } catch (err) {
      const message =
        err instanceof AuthApiError ? err.message : 'Failed to load sessions';
      toast.error(message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSessions();
  }, [loadSessions]);

  const handleLogoutAll = async () => {
    setActionId('all');
    try {
      await logoutAllSessions();
      logoutStore();
      toast.success('Signed out from all devices');
      onLoggedOut?.();
      onClose();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Logout failed');
    } finally {
      setActionId(null);
    }
  };

  const handleLogoutSession = async (id: string) => {
    setActionId(id);
    try {
      await logoutSession(id);
      if (id === sessionId) {
        logoutStore();
        toast.success('Signed out');
        onLoggedOut?.();
        onClose();
        return;
      }
      toast.success('Session ended');
      await loadSessions();
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Failed to end session');
    } finally {
      setActionId(null);
    }
  };

  return (
    <Modal
      open
      onClose={onClose}
      
      title="Active Sessions"
      subtitle="Devices signed in to your account"
      size="lg"
      footer={
        <div className="flex flex-col-reverse gap-3 sm:flex-row">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 text-sm font-medium"
          >
            Close
          </button>
          <button
            type="button"
            onClick={() => void handleLogoutAll()}
            disabled={loading || sessions.length === 0 || actionId === 'all'}
            className="flex-1 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-sm font-semibold disabled:opacity-50"
          >
            {actionId === 'all' ? 'Signing out…' : 'Sign out all devices'}
          </button>
        </div>
      }
    >
      <div className="space-y-3">
        {loading ? (
          <div className="py-12 flex justify-center">
            <i className="ri-loader-4-line animate-spin text-2xl text-slate-400" />
          </div>
        ) : sessions.length === 0 ? (
          <p className="text-center text-sm text-slate-500 py-8">No active sessions</p>
        ) : (
          sessions.map((s) => {
            const isCurrent = s.id === sessionId || s.isCurrent;
            const title =
              s.deviceName ||
              s.deviceModel ||
              [s.browser, s.os].filter(Boolean).join(' on ') ||
              s.userAgent?.split(' ')[0] ||
              'Web browser';
            const detailLine =
              [s.browser, s.os, s.deviceType].filter(Boolean).join(' · ') || null;

            return (
              <div
                key={s.id}
                className={`p-4 rounded-xl border ${
                  isCurrent
                    ? 'border-emerald-300 bg-emerald-50 dark:bg-emerald-950/30 dark:border-emerald-800'
                    : 'border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50'
                }`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="font-semibold text-sm text-slate-900 dark:text-white truncate">
                      {title}
                    </p>
                    {isCurrent && (
                      <span className="inline-flex mt-1 text-[10px] font-semibold text-emerald-700 dark:text-emerald-400">
                        Current session
                      </span>
                    )}
                    {detailLine && detailLine !== title && (
                      <p className="text-xs text-slate-500 mt-1 truncate">{detailLine}</p>
                    )}
                    <p className="text-[11px] text-slate-400 mt-1 truncate font-mono">
                      {s.userAgent || '—'}
                    </p>
                    <p className="text-xs text-slate-400 mt-0.5">
                      Last active: {s.lastAccessed ? new Date(s.lastAccessed).toLocaleString() : '—'}
                    </p>
                    {s.ipAddress ? (
                      <p className="text-xs text-slate-600 dark:text-slate-400 mt-1">
                        <span className="text-slate-400 dark:text-slate-500">IP address: </span>
                        <span className="font-mono">{s.ipAddress}</span>
                      </p>
                    ) : null}
                    {s.ipLocation ? (
                      <p className="text-xs text-slate-600 dark:text-slate-400 mt-0.5">
                        <span className="text-slate-400 dark:text-slate-500">Approx. location: </span>
                        {s.ipLocation}
                      </p>
                    ) : null}
                  </div>
                  <button
                    type="button"
                    disabled={isCurrent || actionId === s.id}
                    onClick={() => void handleLogoutSession(s.id)}
                    className="shrink-0 text-xs font-semibold text-red-600 hover:text-red-700 disabled:opacity-40"
                  >
                    {actionId === s.id ? '…' : isCurrent ? 'Active' : 'Sign out'}
                  </button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </Modal>
  );
}
